import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'

import { Button } from '@/design-system/Button'
import { Badge, Card, Container, Divider, Overline, Skeleton } from '@/design-system/primitives'
import { formatMoney } from '@/lib/money'

import { usePublicEvent } from '@/features/event/api'
import { AddFoodLink } from '@/features/food/components'
import { SingleBraceletCard } from '@/features/single/components'
import { SupplementCard } from '@/features/tickets/SupplementCard'

import { orderQrImageUrl, useMyOrder, useUpdateAttendee } from '../api'
import type { MyAttendee } from '../types'

/**
 * « Mon Masked Night » — la page qu'on ouvre devant le videur.
 *
 * Elle doit fonctionner dans les pires conditions : réseau saturé par 300
 * téléphones, écran à moitié cassé, une main occupée par un verre. D'où **un
 * seul QR, énorme**, tout en haut, et rien d'autre au-dessus de la ligne de
 * flottaison.
 *
 * Un seul QR pour toute la commande : le groupe se présente ensemble, un seul
 * téléphone se sort de la poche. La liste des personnes qu'il couvre est juste
 * en dessous — c'est elle que le contrôle valide, nom par nom.
 */
export function MyTicketsPage() {
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const orderQuery = useMyOrder(token)
  const eventQuery = usePublicEvent()
  const timeZone = eventQuery.data?.timezone ?? 'Europe/Berlin'

  if (!token) {
    return (
      <Container className="py-32">
        <h1 className="font-display text-display-lg text-content-primary">{t('me.noToken')}</h1>
        <p className="mt-4 max-w-lg text-content-secondary">{t('me.noTokenHelp')}</p>
        <Link to="/" className="mt-8 inline-block text-champagne-300 underline">
          {t('order.backHome')}
        </Link>
      </Container>
    )
  }

  if (orderQuery.isPending) {
    return (
      <Container className="py-32">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-8 h-72 w-full max-w-sm" />
      </Container>
    )
  }

  if (orderQuery.isError || !orderQuery.data) {
    return (
      <Container className="py-32">
        <h1 className="font-display text-display-lg text-content-primary">{t('me.invalidLink')}</h1>
        <p className="mt-4 max-w-lg text-content-secondary">{t('me.invalidLinkHelp')}</p>
      </Container>
    )
  }

  const order = orderQuery.data
  const guests = order.tickets.flatMap((ticket) => ticket.attendees)
  // Sans composition (ancienne réponse), on garde le vocabulaire d'origine.
  const contents = order.contents ?? null
  const admits = contents ? contents.admission : true
  const closed = ['cancelled', 'expired', 'refunded', 'partially_refunded'].includes(order.status)
  // Une entrée tenue (réglée ou à régler à la porte) permet d'y ajouter à
  // manger ; une commande en attente de paiement en ligne, pas encore.
  const canAddFood = Boolean(
    contents &&
      (contents.admission || contents.table) &&
      !closed &&
      (order.paid || order.pays_at_door),
  )
  // Les plats ajoutés sous ce même numéro, et ce qu'ils restent à régler.
  const supplements = order.supplements ?? []
  const supplementsDue = order.supplements_due_minor ?? 0
  const money = (minor: number) =>
    formatMoney({ amount_minor: minor, currency: order.currency }, i18n.language)
  const baseLines = order.lines.filter((line) => !line.supplement_sequence)

  return (
    <Container className="py-24">
      <Overline>{t('me.overline')}</Overline>
      <h1 className="mt-3 font-display text-display-lg text-content-primary">
        {admits ? t('me.title') : t('me.titleOrder')}
      </h1>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <span className="font-mono text-sm tabular-nums text-content-muted">
          {order.order_number}
        </span>
        <Badge tone={order.paid ? 'success' : 'ember'}>
          {order.paid ? t('me.paid') : t('me.pending')}
        </Badge>
      </div>

      {!order.paid ? (
        // Tant que le serveur n'a pas constaté le règlement, on le dit
        // franchement : il n'y a pas de QR, et il n'y en aura pas avant.
        <p className="mt-6 max-w-xl rounded-lg border border-ember-400/40 bg-ember-400/5 px-5 py-4 text-sm text-ember-300">
          {admits ? t('me.pendingWarning') : t('me.pendingWarningOrder')}
        </p>
      ) : null}

      {/* Des plats seuls ne font entrer personne : on le dit, et on dit avec
          quelle commande on entre quand elle est connue. */}
      {contents && !contents.admission && !contents.table ? (
        <p className="mt-6 max-w-xl text-sm text-content-secondary">
          {contents.admission_order_number
            ? t('orderContents.linked', { number: contents.admission_order_number })
            : t('orderContents.noAccess')}
        </p>
      ) : null}

      {/* Une table payée reste payée : seuls les plats ajoutés attendent
          leur règlement, et la page le dit sans rien changer au reste. */}
      {supplementsDue > 0 ? (
        <p className="mt-6 max-w-xl rounded-lg border border-ember-400/40 bg-ember-400/5 px-5 py-4 text-sm text-ember-300">
          {t('supplement.due', { amount: money(supplementsDue) })}
        </p>
      ) : null}

      {canAddFood ? (
        <div className="mt-6">
          <AddFoodLink orderNumber={order.order_number} />
        </div>
      ) : null}

      {order.qr_token ? (
        <section className="mt-12">
          <Card className="flex flex-col items-center text-center">
            {/* Fond clair imposé : un QR sombre sur fond sombre ne se scanne pas. */}
            <div className="rounded-md bg-white p-4">
              <img
                src={orderQrImageUrl(token)}
                alt={t('me.qrOrderAlt', { number: order.order_number })}
                className="h-64 w-64"
                width={256}
                height={256}
              />
            </div>
            <p className="mt-6 font-display text-heading-md text-content-primary">
              {t('me.qrOrderTitle')}
            </p>
            <p className="mt-2 max-w-md text-sm text-content-secondary">
              {t('me.qrOrderHelp', { count: guests.length })}
            </p>
          </Card>
        </section>
      ) : null}

      <div className="mt-12 space-y-12">
        {order.tickets.map((ticket) => (
          <section key={ticket.id}>
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <h2 className="font-display text-heading-md text-content-primary">
                {ticket.ticket_type}
              </h2>
              <span className="font-mono text-caption text-content-muted">{ticket.code}</span>
            </div>

            <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {ticket.attendees.map((attendee) => (
                <AttendeeCard
                  key={attendee.id}
                  attendee={attendee}
                  token={token}
                  paid={order.paid}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      {order.tickets.length === 0 && admits ? (
        <p className="mt-10 max-w-xl text-content-secondary">{t('me.ticketsPending')}</p>
      ) : null}

      <Divider className="my-14" />

      <h2 className="font-display text-heading-md text-content-primary">{t('me.orderDetail')}</h2>
      <Card className="mt-6 max-w-xl space-y-3">
        {baseLines.map((line, index) => (
          <div key={`${line.item_type}-${index}`} className="flex justify-between gap-6 text-sm">
            <span className="text-content-secondary">
              {line.quantity} × {line.label}
            </span>
            <span className="tabular-nums text-content-primary">
              {formatMoney(
                { amount_minor: line.line_total_minor, currency: order.currency },
                i18n.language,
              )}
            </span>
          </div>
        ))}
        <div className="flex justify-between gap-6 border-t border-line-subtle pt-4">
          <span className="overline text-content-muted">{t('ticketing.total')}</span>
          <span className="font-display text-heading-md tabular-nums text-champagne-300">
            {formatMoney(
              { amount_minor: order.total_minor, currency: order.currency },
              i18n.language,
            )}
          </span>
        </div>
      </Card>

      {supplements.length > 0 ? (
        <section className="mt-8 max-w-xl">
          <p className="overline text-content-muted">{t('supplement.sectionTitle')}</p>
          <p className="mt-1 text-sm text-content-secondary">{t('supplement.qrSame')}</p>
          <div className="mt-4 space-y-4">
            {supplements.map((row) => (
              <SupplementCard key={row.id} supplement={row} timeZone={timeZone} />
            ))}
          </div>
          <div className="mt-4 flex justify-between gap-6 border-t border-line-subtle pt-4">
            <span className="overline text-content-muted">{t('supplement.grandTotal')}</span>
            <span className="font-display text-heading-md tabular-nums text-content-primary">
              {money(order.grand_total_minor ?? order.total_minor)}
            </span>
          </div>
        </section>
      ) : null}

      <p className="mt-8 max-w-xl text-sm text-content-muted">{t('me.keepLink')}</p>
    </Container>
  )
}

/**
 * Une personne du groupe.
 *
 * Plus de QR ici : il est unique et vit en haut de page. Ce qui reste est ce
 * qui appartient à la personne — son nom, corrigeable tant qu'elle n'est pas
 * entrée, son état à la porte, son droit de vote et son bracelet.
 */
function AttendeeCard({
  attendee,
  token,
  paid,
}: {
  attendee: MyAttendee
  token: string
  paid: boolean
}) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [firstName, setFirstName] = useState(attendee.first_name)
  const [lastName, setLastName] = useState(attendee.last_name)
  const update = useUpdateAttendee(token)

  const name = `${attendee.first_name} ${attendee.last_name}`.trim()

  return (
    <Card className="flex flex-col items-center text-center">
      <p className="font-display text-heading-sm text-content-primary">{name}</p>

      {attendee.checked_in ? (
        <>
          <Badge tone="success">{t('me.checkedIn')}</Badge>
          {/* Le droit de vote est né au check-in : le lien n'apparaît qu'après. */}
          <Link
            to={`/vote?token=${encodeURIComponent(token)}&attendee_id=${attendee.id}`}
            className="mt-3 text-caption text-champagne-300 underline"
          >
            {t('vote.goVote')}
          </Link>
        </>
      ) : paid ? (
        <span className="mt-1 text-caption text-content-muted">{t('me.showAtDoor')}</span>
      ) : (
        <Badge tone="ember">{t('me.pending')}</Badge>
      )}

      {editing ? (
        <form
          className="mt-5 w-full space-y-3 text-left"
          onSubmit={(submitEvent) => {
            submitEvent.preventDefault()
            update.mutate(
              { attendeeId: attendee.id, patch: { first_name: firstName, last_name: lastName } },
              { onSuccess: () => setEditing(false) },
            )
          }}
        >
          <label className="block text-caption text-content-muted">
            {t('ticketing.firstName')}
            <input
              value={firstName}
              onChange={(changeEvent) => setFirstName(changeEvent.target.value)}
              required
              className="mt-1 w-full rounded-md border border-line bg-bg-base px-3 py-2 text-sm text-content-primary"
            />
          </label>
          <label className="block text-caption text-content-muted">
            {t('ticketing.lastName')}
            <input
              value={lastName}
              onChange={(changeEvent) => setLastName(changeEvent.target.value)}
              // Un accompagnant a été nommé par son seul prénom : le corriger
              // ne doit pas obliger à lui inventer un nom de famille.
              required={attendee.is_primary}
              className="mt-1 w-full rounded-md border border-line bg-bg-base px-3 py-2 text-sm text-content-primary"
            />
          </label>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={update.isPending}>
              {t('common.save')}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
              {t('common.cancel')}
            </Button>
          </div>
          {update.isError ? (
            <p className="text-caption text-state-danger">{update.error.message}</p>
          ) : null}
        </form>
      ) : attendee.checked_in ? null : (
        // L'identité se fige au check-in : après l'entrée, plus de bouton.
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="mt-4 text-caption text-champagne-300 underline"
        >
          {t('me.editName')}
        </button>
      )}

      {/* Privé : visible du seul détenteur du lien de commande (RULE 7). */}
      {paid ? <SingleBraceletCard token={token} attendeeId={attendee.id} /> : null}
    </Card>
  )
}
